import React, { useMemo, useState } from 'react';
// import type { Task } from 'src/types/task.type';
import type { Task } from '../types/task.type';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import TaskSummary from './TaskSummary';
import TaskHeatmap from './TaskHeatmap';
import EisenhowerMatrix from './EisenhowerMatrix';

export default function TaskOverview({ tasks, userNameMap }: { tasks: Task[]; userNameMap?: Record<string, string> }) {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const totals = useMemo(() => {
        const total = tasks.length;
        let done = 0,
            low = 0,
            med = 0,
            high = 0,
            na = 0,
            overdue = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (const t of tasks) {
            const isDone = t.status === 'DONE' || !!t.closedAt;
            if (isDone) done++;

            const score = t.priorityScore ?? (t.urgency && t.importance ? t.urgency * t.importance : null);
            if (score == null) na++;
            else if (score <= 8) low++;
            else if (score <= 15) med++;
            else high++;

            if (!isDone && t.dueDate) {
                const due = Date.parse(t.dueDate);
                if (due && due < today.getTime()) overdue++;
            }
        }

        const donePct = total ? Math.round((done / total) * 100) : 0;
        return { total, done, donePct, low, med, high, na, overdue };
    }, [tasks]);

    return (
        <div className="w-full rounded-2xl border bg-white p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium">Task Overview</h3>
                    <button
                        type="button"
                        onClick={() => setIsCollapsed((v) => !v)}
                        className="p-1 rounded hover:bg-gray-100"
                    >
                        {isCollapsed ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />}
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-emerald-200" /> Low <b>({totals.low})</b>
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-amber-200" /> Medium <b>({totals.med})</b>
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <i className="w-3 h-3 rounded bg-rose-200" /> High <b>({totals.high})</b>
                    </span>
                    {totals.na > 0 && (
                        <span className="inline-flex items-center gap-1">
                            <i className="w-3 h-3 rounded bg-gray-200" /> N/A <b>({totals.na})</b>
                        </span>
                    )}
                    <span className="ml-2 text-gray-700">
                        Done: <b>{totals.done}</b> / {totals.total} ({totals.donePct}%)
                    </span>
                    <span className="ml-2 text-rose-600">
                        Overdue: <b>{totals.overdue}</b>
                    </span>
                </div>
            </div>

            {!isCollapsed && (
                <div className="mt-5 flex items-center gap-12">
                    {/* ✅ TaskSummary 왼쪽에 Heatmap 배치 */}
                    <div className="w-[30%]">
                        {/* <TaskHeatmap tasks={tasks} /> */}
                        <EisenhowerMatrix tasks={tasks} />
                    </div>
                    <div className="flex-1">
                        <TaskSummary tasks={tasks} userNameMap={userNameMap} />
                    </div>
                </div>
            )}
        </div>
    );
}
